import ViverseButton, { ViverseButtonSize, ViverseButtonVariants } from '@/components/ViverseButton'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { ReactNode, useEffect } from 'react'

import { cn } from '@/lib/utils'

type TYPE = 'default' | 'viverse' | 'custom'

type AlertDialogProps = {
  open: boolean
  type?: TYPE
  overlay?: boolean
  overlayClassName?: string
  titleIcon?: ReactNode
  title?: string
  content?: ReactNode
  cancelText?: string
  actionText?: string
  onCancel?: () => void
  onAction?: () => void
  /** Called when Radix requests an open-state change, e.g. `false` on Esc. */
  onOpenChange?: (open: boolean) => void
  cancelButtonClassName?: string
  actionButtonClassName?: string
  allowOutsideInteraction?: boolean
  container?: HTMLElement | null
  className?: string
  titleClassName?: string
  buttonSize?: ViverseButtonSize
  buttonContainerClassName?: string
  contentAlign?: 'left' | 'center' | 'right'
}

const BUTTON_VARIANT: Record<
  TYPE,
  { cancel: ViverseButtonVariants; action: ViverseButtonVariants }
> = {
  default: {
    cancel: 'secondary',
    action: 'default',
  },
  viverse: {
    cancel: 'outline',
    action: 'colorful',
  },
  custom: {
    cancel: 'custom',
    action: 'custom',
  },
}

export default function DefaultAlertDialog({
  open = false,
  type = 'default' as TYPE,
  overlay = true,
  overlayClassName,
  titleIcon,
  title = 'Alert',
  content = <p className="body1-omni-styles text-vive-primary-20">Please take action.</p>,
  cancelText = 'Cancel',
  actionText = 'Action',
  cancelButtonClassName,
  actionButtonClassName,
  onCancel,
  onAction,
  onOpenChange,
  allowOutsideInteraction = false,
  container,
  className,
  titleClassName,
  buttonSize = 'medium',
  buttonContainerClassName,
  contentAlign = 'center',
}: AlertDialogProps) {
  useEffect(() => {
    if (allowOutsideInteraction) {
      document.body.style.pointerEvents = 'auto'
    }
  })
  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal container={container ?? undefined}>
        {overlay && (
          <AlertDialog.Overlay
            className={cn('fixed inset-0 z-navigation bg-[#000000B2]', overlayClassName)}
          />
        )}
        <AlertDialog.Content
          className={cn(
            'bg-vive-surface-normal xs:min-w-[312px] xs:min-w-none fixed top-1/2 left-1/2 z-navigation flex h-fit w-fit min-w-[calc(100%-48px)] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-6 overflow-auto rounded-[7px] px-6 py-6 shadow-md backdrop-blur-[50px] md:max-h-[504px] md:min-h-[216px] md:max-w-[640px] md:min-w-[420px] md:gap-8 md:px-10 md:py-8',
            className,
          )}
        >
          <div className="flex flex-col items-center gap-2 md:gap-4">
            <div className="flex flex-col items-center gap-2">
              {titleIcon}
              <AlertDialog.Title
                asChild
                className={cn(
                  'text-vive-white header2-omni-styles truncate text-center',
                  titleClassName,
                )}
              >
                <p>{title}</p>
              </AlertDialog.Title>
            </div>
            <AlertDialog.Description
              asChild
              className={cn(
                contentAlign === 'left' && 'self-start text-left',
                contentAlign === 'right' && 'self-end text-right',
                contentAlign === 'center' && 'text-center',
              )}
            >
              {content}
            </AlertDialog.Description>
          </div>
          <div
            className={cn('flex w-full flex-wrap justify-center gap-4', buttonContainerClassName)}
          >
            {onCancel && (
              <ViverseButton
                className={cancelButtonClassName}
                variant={BUTTON_VARIANT[type].cancel}
                size={buttonSize}
                onClick={onCancel}
              >
                {cancelText}
              </ViverseButton>
            )}
            {onAction && (
              <ViverseButton
                className={actionButtonClassName}
                variant={BUTTON_VARIANT[type].action}
                size={buttonSize}
                onClick={onAction}
              >
                {actionText}
              </ViverseButton>
            )}
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
